import { store, storeActions } from '~/utility/BinSentry-ui-utility';
import { LitEntity, SirenActionMixin, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-action';
import '../../common/bn-flash';
import '../../common/bn-icon';
import '../../common/bn-loading';

class CombineBins extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.binName = '';
  }

  static get properties() {
    return {
      binName: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .button {
        transition: .2s background-color, .2s color, .2s border-color;
      }
      .bin-list {
        display: grid;
        grid-template-columns: repeat(3, 33.33333333% [col-start]);
        margin-bottom: 1em;
      }
      .bin-list[loading] {
        opacity: 0.5;
        pointer-events: none;
        user-select: none;
      }
      .bin {
        text-align: right;
        margin: 5px;
        background-color: white;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .bin.added {
        color: var(--green-30);
        background-color: var(--green-20);
        border-color: var(--green-15);
      }
      .bin[disabled] {
        opacity: 0.5;
      }
      bn-icon {
        float: left;
        margin-right: .5em;
      }
      .complete-container {
        text-align: center;
      }
      label {
        opacity: 1;
        transition: .2s opacity;
        display: block;
        width: 50%;
        margin: auto;
        text-align: left;
      }
      label[disabled] {
        opacity: 0;
      }
      label #name-input {
        width: 100%;
        margin: 0 auto 1em auto;
        display: block;
      }
      .complete-container button {
        display: block;
        margin: auto;
        transition: .2s opacity;
        margin-bottom: .25em;
      }
      .complete-container button[invisible] {
        opacity: 0;
      }
      .no-bins-message {
        text-align: center;
        color: var(--blue-60);
        margin-top: 2em;
      }
    `];
  }

  render() {
    if (this.loading && !this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    let binList = [];
    let addedBinCount = 0;
    if (this.entity && this.entity.entities) {
      binList = this.entity.entities.map((bin) => {
        if (bin.getActionByName('remove-from-aggregate-composition')) {
          addedBinCount++;
        }

        return this._renderBin(bin);
      });
    }

    const enoughBins = addedBinCount > 1;

    return html`
      <div class="bin-list" ?loading="${this.loading}">
        ${binList}
      </div>

      ${renderIf(binList.length < 2 && !this.loading, html`
        <p class="no-bins-message">
          There are not enough bins in this organization to combine.
          <br/>
          Select an organization which directly owns at least two bins, usually a barn/farm.
        </p>
      `)}

      <div class="complete-container">
        <label ?disabled="${!enoughBins}">
          Give this set of tandem bins a name
          <input
            type="text"
            id="name-input"
            value="${this.binName}"
            @input="${(e) => this.binName = e.target.value}}"
            placeholder="Bin Name"
          >
        </label>

        <button
          class="button button--contained button-open-modal add-button"
          @click="${this._complete}"
          ?disabled="${this.loading}"
          ?invisible="${!enoughBins || this.binName.length === 0}"
        >
          Complete
        </button>
      </div>
    `;
  }

  _renderBin(bin) {
    let extraClass = '';
    let iconName = '';
    let disabled = true;

    if (bin.getActionByName('remove-from-aggregate-composition')) {
      extraClass = 'added';
      iconName = 'fas fa-check-square';
      disabled = false;
    }

    if (bin.getActionByName('add-to-aggregate-composition')) {
      iconName = 'far fa-square';
      disabled = false;
    }

    if (this.loading) {
      iconName = 'far fa-spinner fa-pulse';
    }

    return html`
      <button
        @click="${() => this._onBinClick(bin)}"
        class="bin button ${extraClass}"
        title="${bin.properties.name}"
        ?disabled="${disabled}"
      >
        <bn-icon name="${iconName}"></bn-icon>
        ${bin.properties.name}
      </button>
    `;
  }

  async _onBinClick(bin) {
    if (!bin.actions || bin.actions.length === 0) {
      return;
    }

    this.href = bin.actions[0].href;
  }

  async _complete() {
    const action = this.entity.getActionByName('complete-aggregate');

    if (!this.binName || !action) {
      return;
    }

    try {
      this.loading = true;
      const fields = this.getActionFields(action);
      fields.set('name', this.binName);
      const { entity, error } = await this.submitAction(action, fields);
      if (error) {
        this.dispatchEvent(new ErrorEvent('error', { error }));
        this.loading = false;
      } else {
        const binId = entity.getLinkByRel('self').href;
        store.dispatch(storeActions.navigate('/bins/show/overview', { binId }));
      }
    } catch (error) {
      this.dispatchEvent(new ErrorEvent('error', { error }));
      this.loading = false;
    }
  }
}

window.customElements.define('combine-bins', CombineBins);
